import type { AgencyPolicy } from "./policy.ts";

/**
 * CGM's policy: its own defaults, which were National General's for every partner until phase 0
 * (docs/decisions.md, 2026-10-27, issue #139), the rules from its feedback of 2026-10-31 (#186),
 * and its CRMs.
 */
export const CGM_POLICY: AgencyPolicy = {
  partner: "cgm",
  agency: "CGM",
  // Missouri and Kansas (#199).
  timeZone: "America/Chicago",
  rules: [
    {
      id: "ng-service-consent",
      kind: "default",
      carrier: "National General",
      key: "serviceConsent",
      value: "yes",
      basis: "the agency's default",
      stage: "preliminary",
    },
    {
      id: "ng-vehicles-garaged",
      kind: "default",
      carrier: "National General",
      key: "vehiclesGaraged",
      value: 1,
      basis: "the agency's default",
      stage: "preliminary",
    },
    {
      id: "ng-deductible",
      kind: "default",
      carrier: "National General",
      key: "deductible",
      value: "$5,000",
      basis: "no current policy on record; the agency's default",
      stage: "preliminary",
    },
    {
      id: "ng-wind-hail-deductible",
      kind: "default",
      carrier: "National General",
      key: "windHailDeductible",
      value: "2%",
      basis: "no current policy on record; the agency's default",
      stage: "preliminary",
    },
    {
      id: "ng-bundle",
      kind: "default",
      carrier: "National General",
      key: "bundle",
      value: "yes",
      basis:
        "the agency's default, for the bundled price; without a National General auto quote the portal may refuse the package discount",
      stage: "preliminary",
    },
    // CGM's feedback of 2026-10-31 (docs/self-improvement.md#cgms-first-feedback-routed).
    {
      id: "carriers",
      kind: "guidance",
      carrier: "*",
      text: "Quote the carriers the producer names. With none named: National General for an owner-occupied home; Steadily, not National General, for a rented-out property.",
    },
    {
      id: "property-facts-order",
      kind: "source-order",
      carrier: "*",
      keys: ["yearBuilt", "construction", "roofType", "squareFeet", "stories", "units"],
      lookIn:
        "the appraisal in the loan's documents, then the property lookup: the county record and the county's website, then the current policy in the loan's documents (its declarations page), then Zillow; else the default",
    },
    {
      id: "ng-no-licence",
      kind: "strategy",
      carrier: "National General",
      text: "Only when the loan's documents were read and hold no driver's licence for the insured, enter the licence status as never licensed, with 0 years licensed. With no loan read, ask the producer.",
    },
    {
      id: "ng-lower-replacement-cost",
      kind: "strategy",
      carrier: "National General",
      text: "For a lower replacement cost, enter the basement as unfinished (not finished) and the kitchen as Standard, then recalculate the replacement cost.",
    },
    {
      id: "st-building-quality",
      kind: "strategy",
      carrier: "Steadily",
      text: "Choose building quality Economy; the aim is a replacement cost a little above the county's value.",
    },
    {
      id: "st-owner",
      kind: "source-order",
      carrier: "Steadily",
      keys: ["ownerType"],
      lookIn:
        "the current policy's named insured in the loan's documents: a person's name means a person, a company's (an LLC) a company",
    },
    {
      id: "st-owner-default",
      kind: "default",
      carrier: "Steadily",
      key: "ownerType",
      value: "person",
      basis: "the agency's default: a person, unless the current policy names a company",
      stage: "preliminary",
    },
    {
      id: "st-occupancy",
      kind: "default",
      carrier: "Steadily",
      key: "occupancy",
      value: "rented out",
      basis: "the agency's default: every unit rented out",
      stage: "preliminary",
    },
    {
      id: "st-days-uninsured",
      kind: "default",
      carrier: "Steadily",
      key: "daysUninsured",
      value: 0,
      basis:
        "the agency's default: no lapse in cover, assumed even with no current policy on record; the final quote asks",
      stage: "preliminary",
    },
    {
      id: "st-short-term-rental",
      kind: "default",
      carrier: "Steadily",
      key: "shortTermRental",
      value: "no",
      basis: "the agency's default: not a short-term rental",
      stage: "preliminary",
    },
    {
      id: "st-property-manager",
      kind: "default",
      carrier: "Steadily",
      key: "propertyManager",
      value: "no",
      basis: "the agency's default: no property manager",
      stage: "preliminary",
    },
    {
      id: "st-central-alarm",
      kind: "default",
      carrier: "Steadily",
      key: "centralAlarm",
      value: "no",
      basis: "the agency's default: no central station alarm",
      stage: "preliminary",
    },
    {
      id: "st-multi-location",
      kind: "default",
      carrier: "Steadily",
      key: "multiLocation",
      value: "no",
      basis: "the agency's default: no other property to insure with Steadily",
      stage: "preliminary",
    },
    // Every carrier's effective date (2026-11-02): quotes for one client start together.
    ...["National General", "Steadily"].map((carrier) => ({
      id: `${carrier === "Steadily" ? "st" : "ng"}-effective-date`,
      kind: "derive" as const,
      carrier,
      key: "effectiveDate",
      derive: ["sameAsWithin:priorPolicyExpiration:60", "daysAhead:14"] as const,
      basis:
        "the agency's policy: when the current policy ends, if within 60 days; else two weeks from today",
      stage: "always" as const,
    })),
  ],
  crms: [
    { name: "Arive", aliases: ["myarive"] },
    { name: "LendingPad", aliases: ["Lending Pad"] },
  ],
};
